/**
 * T775 — "was the app already alive when this broke?"
 *
 * A route error boundary that renders *instead of* the app looks identical, in
 * Sentry, to one that renders *after* a click three minutes in. The 483 events of
 * `JAVASCRIPT-REACT-3` could not tell those apart, and they are different bugs:
 * the first is hydration (a route module that failed to load, a server/client
 * mismatch), the second is a navigation.
 *
 * One module-scope boolean, flipped once. `App` in `root.tsx` calls
 * `markHydrated()` from a mount effect, so the flag is only ever cleared by the
 * app actually mounting. A boundary that replaced the app therefore still reads
 * `isHydrating() === true` — which is the answer we want, obtained by the app's
 * silence rather than by asking React for something it does not expose.
 *
 * On the server every render is a fresh module instance, so `isHydrating()` is
 * true there too. That is also correct: an SSR boundary render is, by definition,
 * before hydration.
 */
let hydrated = false;

/** Called once from root's `App` mount effect. Idempotent (StrictMode calls it twice). */
export function markHydrated(): void {
  hydrated = true;
}

/** True until root's `App` has mounted at least once in this document. */
export function isHydrating(): boolean {
  return !hydrated;
}

/** Test seam only — resets the module-scope flag between cases. */
export function resetHydrationStateForTests(): void {
  hydrated = false;
}
